import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { CircleMarker, MapContainer, TileLayer } from "react-leaflet";
import { OfficeLocationPickerMap } from "../components/OfficeLocationPickerMap";
import { EmptyState } from "../components/orderKit";
import {
  MerchantCategoryChips,
  MerchantCategoryPicker,
  labelsForCategoryCodes,
} from "../components/MerchantCategoryPicker";
import {
  apiUrl,
  changeMerchantPortalPassword,
  getMerchantProfile,
  getMerchantToken,
  listMerchantBusinessCategories,
  patchMerchantPortalProfile,
  reverseGeocodeLatLng,
  setDefaultDeliveryFeePayer,
  uploadMerchantProfileImage,
} from "../services/api";
import { useMerchantStore } from "../store/useMerchantStore";
import { validateMerchantPortalPassword } from "../utils/merchantPortalPassword";
import { validateMerchantPortalImageFile, safeProductOrProfileImageSrc } from "../utils/safeMerchantImageFile";
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";

function readProfileIntoForm(p, setters) {
  if (!p) return;
  const {
    setSelected,
    setOfficeAddress,
    setOfficeLat,
    setOfficeLng,
    setContactPerson,
    setContactPhone,
  } = setters;
  const codes = Array.isArray(p.businessCategories) ? p.businessCategories : [];
  setSelected(new Set(codes.map((c) => String(c).toUpperCase())));
  setOfficeAddress(p.officeAddress ?? "");
  setOfficeLat(p.officeLatitude != null ? String(p.officeLatitude) : "");
  setOfficeLng(p.officeLongitude != null ? String(p.officeLongitude) : "");
  setContactPerson(p.contactPerson ?? "");
  setContactPhone(p.contactPhone ?? "");
}

export function StoreProfile() {
  const merchantId = useMerchantStore((s) => s.merchantId);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [profile, setProfile] = useState(null);
  const [payerSaving, setPayerSaving] = useState(false);
  const [payerError, setPayerError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [officeAddress, setOfficeAddress] = useState("");
  const [officeLat, setOfficeLat] = useState("");
  const [officeLng, setOfficeLng] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  /** Start in view mode; edit reveals the form (only verified merchants can sign in). */
  const [editing, setEditing] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMessage, setPwMessage] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [notice, setNotice] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  /** Fill the address field from the pin (Google reverse-geocode via backend). */
  async function fillAddressFromPin() {
    const la = parseFloat(officeLat);
    const lo = parseFloat(officeLng);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return;
    setGeocoding(true);
    try {
      const r = await reverseGeocodeLatLng(la, lo);
      if (r.formattedAddress) setOfficeAddress(r.formattedAddress);
      else setError(t("store.no_address_for_pin"));
    } catch (e) {
      setError(t("store.address_lookup_failed"));
    } finally {
      setGeocoding(false);
    }
  }

  const load = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    setError("");
    try {
      const [cats, p] = await Promise.all([
        listMerchantBusinessCategories(),
        getMerchantProfile(merchantId),
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      setProfile(p);
      readProfileIntoForm(p, {
        setSelected,
        setOfficeAddress,
        setOfficeLat,
        setOfficeLng,
        setContactPerson,
        setContactPhone,
      });
      setEditing(false);
    } catch (e) {
      setError(friendlyError(e, t("store.load_failed")));
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  async function submitPortalPasswordChange(ev) {
    ev.preventDefault();
    setPwMessage(null);
    if (!merchantId) return;
    const err = validateMerchantPortalPassword(pwNew);
    if (err) {
      setPwMessage({ text: err, ok: false });
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwMessage({ text: t("forgot.mismatch"), ok: false });
      return;
    }
    setPwSaving(true);
    try {
      await changeMerchantPortalPassword(merchantId, pwCurrent, pwNew);
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
      setPwMessage({ text: t("store.password_updated"), ok: true });
    } catch (e) {
      setPwMessage({ text: friendlyError(e, t("store.password_failed")), ok: false });
    } finally {
      setPwSaving(false);
    }
  }

  function toggleCode(code) {
    const c = String(code).toUpperCase();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }

  function beginEdit() {
    if (!profile) return;
    readProfileIntoForm(profile, {
      setSelected,
      setOfficeAddress,
      setOfficeLat,
      setOfficeLng,
      setContactPerson,
      setContactPhone,
    });
    setEditing(true);
    setError("");
  }

  function cancelEdit() {
    if (profile) {
      readProfileIntoForm(profile, {
        setSelected,
        setOfficeAddress,
        setOfficeLat,
        setOfficeLng,
        setContactPerson,
        setContactPhone,
      });
    }
    setEditing(false);
    setError("");
  }

  async function save(e) {
    e.preventDefault();
    if (!merchantId) return;
    setError("");
    if (selected.size === 0) {
      setError(t("store.select_category"));
      return;
    }
    setSaving(true);
    try {
      const body = {
        businessCategories: Array.from(selected),
        officeAddress: officeAddress.trim(),
        officeLatitude: officeLat.trim() ? parseFloat(officeLat) : null,
        officeLongitude: officeLng.trim() ? parseFloat(officeLng) : null,
        contactPerson: contactPerson.trim(),
        contactPhone: contactPhone.trim(),
      };
      const updated = await patchMerchantPortalProfile(merchantId, body);
      setProfile(updated);
      setEditing(false);
      setError("");
      setNotice(t("store.saved"));
    } catch (err) {
      setError(friendlyError(err, t("store.save_failed")));
    } finally {
      setSaving(false);
    }
  }

  async function onProfileImage(ev) {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f || !merchantId || !editing) return;
    setError("");
    const check = await validateMerchantPortalImageFile(f, { maxBytes: 5 * 1024 * 1024 });
    if (!check.ok) {
      setError(check.message);
      return;
    }
    setUploadingPhoto(true);
    try {
      await uploadMerchantProfileImage(merchantId, f);
      // Refresh the photo without leaving edit mode or discarding unsaved form edits.
      const p = await getMerchantProfile(merchantId);
      setProfile(p);
      setNotice(t("store.photo_updated"));
    } catch (err) {
      setError(friendlyError(err, t("store.upload_failed")));
    } finally {
      setUploadingPhoto(false);
    }
  }

  if (!getMerchantToken() || !merchantId) {
    return <Navigate to="/login" replace />;
  }

  const rawImg =
    profile?.profileImageUrl && String(profile.profileImageUrl).startsWith("/")
      ? apiUrl(profile.profileImageUrl)
      : profile?.profileImageUrl || null;
  const imgSrc = rawImg ? safeProductOrProfileImageSrc(rawImg, null) : null;

  const categoryLabels = profile
    ? labelsForCategoryCodes(categories, profile.businessCategories || [])
    : [];

  const hasOfficePin = profile?.officeLatitude != null && profile?.officeLongitude != null;
  const phoneDigits = String(profile?.contactPhone || "").replace(/[^\d+]/g, "");

  if (loading && !profile) {
    return (
      <div className="page-container">
        <div className="loading">{t("store.loading")}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page-container">
        <EmptyState
          icon="fa-store"
          title={t("store.unavailable")}
          message={error || t("store.load_failed")}
          action={
            <button type="button" onClick={load}>
              {t("common.try_again")}
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="page-container store-page">
      <section className="store-cover" style={imgSrc ? { backgroundImage: `url("${imgSrc}")` } : undefined}>
        <div className="store-cover__shade" />
        <div className="store-cover__actions">
          {!editing ? (
            <button type="button" className="store-cover__button" onClick={beginEdit}>
              <i className="fa-solid fa-pen" aria-hidden="true" /> {t("store.edit")}
            </button>
          ) : (
            <label className={`store-cover__button${uploadingPhoto ? " store-cover__button--busy" : ""}`}>
              <i className={`fa-solid ${uploadingPhoto ? "fa-spinner fa-spin" : "fa-camera"}`} aria-hidden="true" />{" "}
              {uploadingPhoto ? t("store.uploading") : imgSrc ? t("store.change_photo") : t("store.add_photo")}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,.heic,.heif"
                onChange={onProfileImage}
                disabled={uploadingPhoto}
                hidden
              />
            </label>
          )}
        </div>
        <div className="store-cover__content">
          <div className="eyebrow" style={{ color: "var(--gold-light)" }}>{t("store.your_storefront")}</div>
          <h1 className="store-cover__name">
            {profile.name}
            <i className="fa-solid fa-circle-check store-cover__verified" title={t("store.verified_by_odos")} aria-label={t("store.verified")} />
          </h1>
          {categoryLabels.length > 0 ? (
            <div className="store-cover__chips">
              {categoryLabels.map((l) => (
                <span key={l} className="store-cover__chip">
                  {l}
                </span>
              ))}
            </div>
          ) : profile.businessType ? (
            <div className="store-cover__chips">
              <span className="store-cover__chip">{profile.businessType}</span>
            </div>
          ) : null}
        </div>
      </section>

      {error ? (
        <div className="alert alert--error">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
        </div>
      ) : null}
      {notice ? (
        <div className="alert alert--success" role="status">
          <i className="fa-solid fa-circle-check" aria-hidden="true" /> {notice}
        </div>
      ) : null}

      <div className="store-layout">
        <div className="store-layout__main">
          {!editing ? (
            <>
              <section className="panel store-section">
                <h2 className="store-section__title">{t("store.shop")}</h2>
                <InfoRow icon="fa-location-dot" label={t("store.shop_location")}>
                  {profile.officeAddress ? (
                    <div className="store-value">{profile.officeAddress}</div>
                  ) : (
                    <div className="store-value store-value--empty">{t("store.not_set")}</div>
                  )}
                  {hasOfficePin ? (
                    <>
                      <LocationPreview lat={profile.officeLatitude} lng={profile.officeLongitude} />
                      <a
                        className="store-link"
                        href={`https://www.google.com/maps/search/?api=1&query=${profile.officeLatitude},${profile.officeLongitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" /> {t("store.open_in_maps")}
                      </a>
                    </>
                  ) : null}
                </InfoRow>
                <InfoRow icon="fa-user-tie" label={t("store.contact_person")}>
                  <div className={`store-value${profile.contactPerson ? "" : " store-value--empty"}`}>
                    {profile.contactPerson || t("store.not_set")}
                  </div>
                </InfoRow>
                <InfoRow icon="fa-phone" label={t("store.contact_phone")}>
                  {profile.contactPhone ? (
                    <a className="store-value store-value--link" href={`tel:${phoneDigits}`}>
                      {profile.contactPhone}
                    </a>
                  ) : (
                    <div className="store-value store-value--empty">{t("store.not_set")}</div>
                  )}
                </InfoRow>
                <InfoRow icon="fa-tags" label={t("store.categories")} last>
                  {categoryLabels.length > 0 ? (
                    <MerchantCategoryChips labels={categoryLabels} />
                  ) : (
                    <div className="store-value store-value--empty">{profile.businessType || t("store.not_set")}</div>
                  )}
                </InfoRow>
              </section>

              <section className="panel store-section">
                <h2 className="store-section__title">
                  {t("store.legal")}
                  <span className="store-lock">
                    <i className="fa-solid fa-lock" aria-hidden="true" /> {t("store.read_only")}
                  </span>
                </h2>
                <div className="store-legal-grid">
                  <InfoRow icon="fa-building" label={t("store.business_name")}>
                    <div className="store-value">{profile.name || "-"}</div>
                  </InfoRow>
                  <InfoRow icon="fa-envelope" label={t("login.email_label")}>
                    <div className="store-value">{profile.contactEmail || "-"}</div>
                  </InfoRow>
                  <InfoRow icon="fa-receipt" label={t("store.tin")} last>
                    <div className="store-value">{profile.tin || "-"}</div>
                  </InfoRow>
                  <InfoRow icon="fa-file-contract" label={t("store.registration_number")} last>
                    <div className="store-value">{profile.registrationNumber || "-"}</div>
                  </InfoRow>
                </div>
                <p className="store-footnote">
                  {t("store.legal_note")}
                </p>
              </section>
            </>
          ) : (
            <form id="store-profile-edit-form" onSubmit={save} className="store-edit">
              <section className="panel store-section">
                <MerchantCategoryPicker
                  theme="portal"
                  idPrefix="portal-cat"
                  options={categories}
                  selected={selected}
                  onToggle={toggleCode}
                />
              </section>

              <section className="panel store-section">
                <h2 className="store-section__title">{t("store.shop_location")}</h2>
                <p className="store-section__hint">{t("store.shop_location_hint")}</p>
                <OfficeLocationPickerMap
                  latitude={officeLat}
                  longitude={officeLng}
                  onLocationChange={(la, lo) => {
                    setOfficeLat(la);
                    setOfficeLng(lo);
                  }}
                />
                <div className="store-address-row">
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <label className="field-label" htmlFor="store-office-address">
                      {t("store.shop_address")}
                    </label>
                    <input
                      id="store-office-address"
                      value={officeAddress}
                      onChange={(ev) => setOfficeAddress(ev.target.value)}
                      placeholder={t("store.shop_address_placeholder")}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={fillAddressFromPin}
                    disabled={geocoding || !officeLat.trim() || !officeLng.trim()}
                    title={t("store.lookup_pin_address")}
                  >
                    <i className={`fa-solid ${geocoding ? "fa-spinner fa-spin" : "fa-location-crosshairs"}`} aria-hidden="true" />{" "}
                    {t("store.use_pin_address")}
                  </button>
                </div>
                <details className="coords-details">
                  <summary>{t("map.manual_coords")}</summary>
                  <div className="coords-grid">
                    <div>
                      <label className="field-label" htmlFor="store-lat">
                        {t("map.latitude")}
                      </label>
                      <input id="store-lat" inputMode="decimal" value={officeLat} onChange={(ev) => setOfficeLat(ev.target.value)} />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="store-lng">
                        {t("map.longitude")}
                      </label>
                      <input id="store-lng" inputMode="decimal" value={officeLng} onChange={(ev) => setOfficeLng(ev.target.value)} />
                    </div>
                  </div>
                </details>
              </section>

              <section className="panel store-section">
                <h2 className="store-section__title">{t("store.contact")}</h2>
                <div className="store-form-grid">
                  <div>
                    <label className="field-label" htmlFor="store-contact-person">
                      {t("store.contact_person")}
                    </label>
                    <input id="store-contact-person" value={contactPerson} onChange={(ev) => setContactPerson(ev.target.value)} />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="store-contact-phone">
                      {t("store.contact_phone")}
                    </label>
                    <input
                      id="store-contact-phone"
                      type="tel"
                      value={contactPhone}
                      onChange={(ev) => setContactPhone(ev.target.value)}
                    />
                  </div>
                </div>
              </section>

              <section className="panel store-section store-section--muted">
                <h2 className="store-section__title">
                  {t("store.legal")}
                  <span className="store-lock">
                    <i className="fa-solid fa-lock" aria-hidden="true" /> {t("store.not_editable")}
                  </span>
                </h2>
                <div className="store-form-grid">
                  {[
                    [t("store.business_name"), profile.name],
                    [t("login.email_label"), profile.contactEmail],
                    [t("store.tin_full"), profile.tin],
                    [t("store.registration_number"), profile.registrationNumber],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <span className="field-label">{label}</span>
                      <input readOnly disabled value={value ?? ""} placeholder="-" />
                    </div>
                  ))}
                </div>
              </section>

              <div className="store-save-bar">
                <span className="muted" style={{ fontSize: 13 }}>
                  {t("store.editing")}
                </span>
                <div style={{ display: "flex", gap: 10 }}>
                  <button type="button" onClick={cancelEdit} disabled={saving}>
                    {t("common.cancel")}
                  </button>
                  <button type="submit" className="primary" disabled={saving}>
                    {saving ? t("store.saving") : t("store.save_changes")}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        <aside className="store-layout__side">
          <section className="panel store-section">
            <h2 className="store-section__title">
              <i className="fa-solid fa-motorcycle store-section__icon" aria-hidden="true" /> {t("store.who_pays")}
            </h2>
            <p className="store-section__hint">
              {t("store.who_pays_hint")}
            </p>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[
                ["SENDER", t("fee.i_pay_at_pickup")],
                ["RECEIVER", t("fee.receivers_pay")],
              ].map(([value, label]) => {
                const active = (profile.defaultDeliveryFeePayer || "SENDER") === value;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={payerSaving || active}
                    onClick={async () => {
                      setPayerSaving(true);
                      setPayerError("");
                      try {
                        setProfile(await setDefaultDeliveryFeePayer(merchantId, value));
                      } catch (err) {
                        setPayerError(friendlyError(err));
                      } finally {
                        setPayerSaving(false);
                      }
                    }}
                    style={{
                      padding: "8px 14px",
                      borderRadius: "10px",
                      border: active ? "2px solid #6b4eaa" : "1px solid #d7cdef",
                      background: active ? "#f3effb" : "#ffffff",
                      fontWeight: active ? 700 : 500,
                      cursor: active || payerSaving ? "default" : "pointer",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {payerError && <p style={{ color: "#a63a3a", fontSize: "13px" }}>{payerError}</p>}
          </section>

          <section className="panel store-section">
            <h2 className="store-section__title">
              <i className="fa-solid fa-shield-halved store-section__icon" aria-hidden="true" /> {t("store.security")}
            </h2>
            <p className="store-section__hint">
              {t("store.security_hint", { email: profile.contactEmail || t("store.your_contact_email") })}
            </p>
            <form onSubmit={submitPortalPasswordChange} className="store-password">
              <div>
                <label className="field-label" htmlFor="pw-current">
                  {t("store.current_password")}
                </label>
                <input
                  id="pw-current"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={pwCurrent}
                  onChange={(ev) => setPwCurrent(ev.target.value)}
                />
              </div>
              <div>
                <label className="field-label" htmlFor="pw-new">
                  {t("forgot.new_password")}
                </label>
                <input
                  id="pw-new"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  value={pwNew}
                  onChange={(ev) => setPwNew(ev.target.value)}
                />
              </div>
              <ul className="pw-rules" aria-label={t("store.password_requirements")}>
                {PASSWORD_RULES.map((r) => {
                  const ok = r.test(pwNew);
                  return (
                    <li key={r.label} className={ok ? "pw-rules__ok" : ""}>
                      <i className={`fa-solid ${ok ? "fa-circle-check" : "fa-circle"}`} aria-hidden="true" /> {t(r.label)}
                    </li>
                  );
                })}
              </ul>
              <div>
                <label className="field-label" htmlFor="pw-confirm">
                  {t("forgot.confirm_password")}
                </label>
                <input
                  id="pw-confirm"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  value={pwConfirm}
                  onChange={(ev) => setPwConfirm(ev.target.value)}
                />
                {pwConfirm && pwNew !== pwConfirm ? <div className="field-error">{t("store.no_match_yet")}</div> : null}
              </div>
              <label className="pw-show">
                <input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} /> {t("store.show_passwords")}
              </label>
              {pwMessage ? (
                <div className={`alert ${pwMessage.ok ? "alert--success" : "alert--error"}`} style={{ margin: 0 }}>
                  {pwMessage.text}
                </div>
              ) : null}
              <button type="submit" className="primary" disabled={pwSaving} style={{ alignSelf: "flex-start" }}>
                {pwSaving ? t("forgot.updating") : t("forgot.update")}
              </button>
            </form>
          </section>
        </aside>
      </div>
    </div>
  );
}

const PASSWORD_RULES = [
  { label: "forgot.password_hint", test: (p) => p.length >= 8 },
  { label: "store.rule_upper", test: (p) => /[A-Z]/.test(p) },
  { label: "store.rule_lower", test: (p) => /[a-z]/.test(p) },
  { label: "store.rule_number", test: (p) => /[0-9]/.test(p) },
  { label: "store.rule_special", test: (p) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?/~`]/.test(p) },
];

function InfoRow({ icon, label, children, last }) {
  return (
    <div className={`info-row${last ? " info-row--last" : ""}`}>
      <i className={`fa-solid ${icon} info-row__icon`} aria-hidden="true" />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="info-row__label">{label}</div>
        {children}
      </div>
    </div>
  );
}

/** Small read-only map of the shop pin. */
function LocationPreview({ lat, lng }) {
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  return (
    <div className="location-preview">
      <MapContainer
        center={[la, ln]}
        zoom={16}
        style={{ height: "100%", width: "100%" }}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        zoomControl={false}
        attributionControl={false}
        keyboard={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <CircleMarker
          center={[la, ln]}
          radius={9}
          pathOptions={{ color: "#ffffff", weight: 3, fillColor: "#6b4eaa", fillOpacity: 1 }}
        />
      </MapContainer>
    </div>
  );
}
